import { AnalyticsEvents } from '@mondaygpt/analytics-events'
import { BUSINESS_DELETE_BLOCKED_REASON, BusinessRole } from '@mondaygpt/constants'
import { Button } from 'components/Button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/Dialog'
import { useAnalytics } from 'hooks'
import { reloadToRoot } from 'lib/utils'
import { Trash2 } from 'lucide-react'
import { selectCurrentBusinessRole } from 'modules/authentication'
import { RevenueCatService } from 'modules/billing/services/revenuecat.service'
import {
  selectBusinessEntitlement,
  selectHasBillableSubscription,
} from 'modules/billing/slices/credit-balance.slice'
import { deleteCurrentBusinessAction, selectCurrentBusiness } from 'modules/business'
import { useAppDispatch, useAppSelector } from 'modules/core'
import { useCallback, useState } from 'react'

/**
 * Owner-only "delete this business" section at the end of the billing page.
 * Owns the full delete flow: cancel-first guidance while a subscription is
 * actively billing, destructive confirmation otherwise, and the structured
 * 409 fallback for stale billing state.
 */
export function DeleteBusinessCard() {
  const dispatch = useAppDispatch()
  const { track } = useAnalytics()
  const currentBusiness = useAppSelector(selectCurrentBusiness)
  const businessRole = useAppSelector(selectCurrentBusinessRole)
  const entitlement = useAppSelector(selectBusinessEntitlement)
  const hasBillableSubscription = useAppSelector(selectHasBillableSubscription)

  const [confirmOpen, setConfirmOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showCancelFirst, setShowCancelFirst] = useState(false)

  /**
   * Delete entry point. If the business is still actively billing, show the
   * cancel-first dialog up front instead of the delete confirmation — the
   * server would reject the delete anyway (the 409 below is the fallback for
   * stale billing state). Cancelled-but-paid falls through to the normal
   * confirmation and may delete.
   */
  const handleDeleteTapped = useCallback(() => {
    if (hasBillableSubscription) {
      setShowCancelFirst(true)
      return
    }
    setError(null)
    setConfirmOpen(true)
  }, [hasBillableSubscription])

  const closeConfirm = useCallback(() => {
    if (busy) return
    setConfirmOpen(false)
    setError(null)
  }, [busy])

  const confirmDelete = useCallback(async () => {
    setBusy(true)
    setError(null)
    try {
      await dispatch(deleteCurrentBusinessAction()).unwrap()
      reloadToRoot()
    } catch (err) {
      // .unwrap() throws the rejectWithValue payload (a plain {message, reason}
      // object, not an Error) — read off either shape so the 409 billing-gate
      // guidance reaches the user instead of the fallback.
      const payload = err as { message?: string; reason?: string }
      if (payload?.reason === BUSINESS_DELETE_BLOCKED_REASON) {
        // Server refused: the business is still actively billed. This 409 is the
        // designed happy path for subscribed users (server logs it without
        // paging), so don't console.error it into the console bridge. Swap the
        // delete confirmation for the cancel-first guidance dialog.
        setConfirmOpen(false)
        setShowCancelFirst(true)
        setBusy(false)
        return
      }
      // Genuinely unexpected failure — surface it to error tracking via the console bridge.
      console.error('Failed to delete business:', err)
      setError(payload?.message || 'Something went wrong. Try again.')
      setBusy(false)
    }
  }, [dispatch])

  const handleManageSubscription = useCallback(async () => {
    track(AnalyticsEvents.Billing.BILLING_MANAGE_SUBSCRIPTION_TAPPED, {
      tier: entitlement?.tier || 'Free',
    })
    const service = RevenueCatService.getInstance()
    await service.openManageSubscriptions()
  }, [track, entitlement?.tier])

  if (!currentBusiness || businessRole !== BusinessRole.Owner) return null

  return (
    <>
      <div className="rounded-2xl border border-line bg-bg-2 p-6">
        <h3 className="text-sm font-semibold text-paper">Delete this business</h3>
        <p className="mt-1 text-[13px] text-paper-dim">
          Permanently removes "{currentBusiness.name}", its brand kit, posts, and credit balance.
          Cannot be undone.
        </p>
        <Button
          type="button"
          variant="t3-destructive"
          size="t3-md"
          className="mt-4"
          onClick={handleDeleteTapped}
          data-testid="billing-delete-business"
        >
          <Trash2 size={14} />
          Delete business
        </Button>
      </div>

      <Dialog
        open={confirmOpen}
        onOpenChange={open => {
          if (!open) closeConfirm()
        }}
      >
        <DialogContent variant="dark" size="sm" className="max-w-[420px]">
          <DialogHeader>
            <DialogTitle className="text-[16px] font-semibold text-paper">
              {`Delete "${currentBusiness.name}"?`}
            </DialogTitle>
            <DialogDescription className="text-[13px] text-paper-dim">
              This permanently removes the business, brand kit, posts, and credit balance. If you
              have paid time remaining on a cancelled plan, deleting forfeits it. Collaborators lose
              access. Cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {error && <p className="text-[12px] text-[color:var(--alert-error-text)]">{error}</p>}
          <DialogFooter className="mt-2 flex-row justify-end gap-2">
            <Button
              type="button"
              variant="t3-tertiary"
              size="t3-md"
              onClick={closeConfirm}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="t3-destructive"
              size="t3-md"
              onClick={confirmDelete}
              disabled={busy}
              data-testid="billing-delete-business-confirm"
            >
              {busy ? 'Deleting…' : 'Delete forever'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showCancelFirst} onOpenChange={setShowCancelFirst}>
        <DialogContent
          variant="dark"
          size="sm"
          className="max-w-[420px]"
          data-testid="business-delete-cancel-first"
        >
          <DialogHeader>
            <DialogTitle className="text-[16px] font-semibold text-paper">
              Cancel your subscription first
            </DialogTitle>
            <DialogDescription className="text-[13px] text-paper-dim">
              {`This business has an active ${entitlement?.tier ?? 'paid'} plan. We don't cancel subscriptions for you. Cancel it in the billing portal, then come back and delete the business.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-2 flex-row justify-end gap-2">
            <Button
              type="button"
              variant="t3-tertiary"
              size="t3-md"
              onClick={() => setShowCancelFirst(false)}
            >
              Close
            </Button>
            <Button
              type="button"
              variant="t3-primary"
              size="t3-md"
              onClick={handleManageSubscription}
              data-testid="business-delete-manage-subscription"
            >
              Manage subscription
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
